import React, { Component, ReactNode } from 'react';
import * as Sentry from '@sentry/react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Global Error Boundary - Catches unhandled React errors
 * Prevents the app from being stuck on the green splash screen
 * by removing it and showing a friendly retry screen.
 */
class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('[ErrorBoundary] Caught error:', error, errorInfo);

    // 🩺 Envoyer l'erreur + le component stack à Sentry (no-op si le DSN n'est pas configuré)
    Sentry.captureException(error, {
      extra: { componentStack: errorInfo.componentStack },
    });

    // Remove the splash screen so the user isn't stuck on green
    const shell = document.getElementById('app-shell');
    if (shell) shell.remove();

    const root = document.getElementById('root');
    if (root) root.style.display = 'block';
  }

  handleRetry = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div style={styles.container}>
          <div style={styles.card}>
            <img
              src="/singequimarcheorange.png"
              alt="Monkey"
              style={styles.monkey}
            />
            <h1 style={styles.title}>A cheeky monkey got into the code!</h1>
            <p style={styles.message}>
              Don't worry, a quick refresh should chase it away.
            </p>
            <button
              style={styles.button}
              onClick={this.handleRetry}
              onMouseOver={(e) => {
                (e.target as HTMLButtonElement).style.opacity = '0.9';
                (e.target as HTMLButtonElement).style.transform = 'translateY(-1px)';
              }}
              onMouseOut={(e) => {
                (e.target as HTMLButtonElement).style.opacity = '1';
                (e.target as HTMLButtonElement).style.transform = 'translateY(0)';
              }}
            >
              Refresh the app
            </button>
            <p style={styles.hint}>
              If it keeps happening, try clearing your browser cache
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Inline styles to avoid dependency on app CSS (which might have failed to load)
const styles: Record<string, React.CSSProperties> = {
  container: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: '#f9f4ee',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: "'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    padding: '20px',
  },
  card: {
    textAlign: 'center' as const,
    maxWidth: '380px',
    width: '100%',
  },
  monkey: {
    width: '120px',
    height: 'auto',
    marginBottom: '16px',
  },
  title: {
    color: '#1D6E64',
    fontSize: '20px',
    fontWeight: 600,
    margin: '0 0 12px',
  },
  message: {
    color: '#555',
    fontSize: '15px',
    margin: '0 0 24px',
    lineHeight: '1.5',
  },
  button: {
    background: '#1D6E64',
    color: '#fff',
    border: 'none',
    padding: '14px 32px',
    borderRadius: '12px',
    fontSize: '15px',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
    transition: 'opacity 0.2s, transform 0.2s',
    boxShadow: '0 2px 8px rgba(29, 110, 100, 0.25)',
  },
  hint: {
    color: '#999',
    fontSize: '12px',
    margin: '16px 0 0',
  },
};

export default ErrorBoundary;
